import React from "react";
import styled from "@emotion/styled";
import { DateTime } from "luxon";
import { Flex, Text } from "styles/utility-components";

import Alert from "components/ui/alert/Alert";
import { NoticeDetails } from "lib/hooks/api/notices/useNotice";
import AttributeDisplay from "./AttributeDisplay";
import { CpvCodesContainer } from "./CpvCodes";

type LotsV2Props = {
  lotsv2: NoticeDetails["lotsv2"];
};

type LotDetailsProps = {
  lot: NoticeDetails["lotsv2"][0];
};

function LotDetails({ lot }: LotDetailsProps) {
  return (
    <Container gap="16px" column>
      <Flex gap="8px" column>
        <Flex gap="4px" column>
          <Text description color="sysTextPlaceholder">
            Lot
          </Text>
          <Text h3>{lot.title}</Text>
        </Flex>
        {lot.description && <Text color="sysTextSecondary">{lot.description}</Text>}
      </Flex>
      {lot.cpvCodes.length !== 0 && <CpvCodesContainer cpvCodes={lot.cpvCodes} />}
      <AttributeDisplay attribute="Value" money={lot.value} />
      <AttributeDisplay attribute="Start" date={lot.contractPeriod?.startDate} />
      <AttributeDisplay attribute="End" date={lot.contractPeriod?.endDate} />
      {lot.contractPeriod?.maxExtentDate && lot.contractPeriod?.endDate && (
        <Alert type="warning">
          Option for extension up to{" "}
          {DateTime.fromISO(lot.contractPeriod.maxExtentDate)
            .diff(DateTime.fromISO(lot.contractPeriod.endDate))
            .toFormat("M")}{" "}
          months.
        </Alert>
      )}
    </Container>
  );
}

const Container = styled(Flex)(({ theme }) => ({
  paddingLeft: "24px",
  borderLeft: `2px solid ${theme.colors.sysBorderDecorative}`,
}));

export default function LotsV2({ lotsv2 }: LotsV2Props) {
  return (
    <Flex column gap="24px">
      <Text h2>Lot Details</Text>
      {!lotsv2.length ? (
        <Text>No lots associated with this notice</Text>
      ) : (
        lotsv2.map((lot) => <LotDetails key={lot.id} lot={lot} />)
      )}
    </Flex>
  );
}
